// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useCallback, useMemo, useRef } from "react";
import {
  FormControl,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuProps,
  forwardRef,
  useMergeRefs,
  useMultiStyleConfig,
} from "@chakra-ui/react";

import { TextField, TextFieldProps } from "../text-field";
import { ArrowDownIcon } from "../icons";

export interface SelectProps extends TextFieldProps {
  menuProps?: MenuProps;
  menuListProps?: object;
  multiple?: boolean;
  isDisabled?: boolean;
  options?: any[];
  value?: any;
  valueKey?: string;
  defaultValue?: any;
  labelKey?: string;
  placeholder?: string;
  label: string;
  isFullWidth?: boolean;
  onChange?: (value: any | any[]) => void;
}

export const Select = forwardRef<SelectProps, "input">(
  (
    {
      menuProps,
      menuListProps = {},
      multiple = false,
      isDisabled = false,
      options = [],
      value,
      valueKey,
      defaultValue,
      labelKey,
      onChange,
      label,
      placeholder = null,
      size,
      isFullWidth = true,
      sx,
      ...rest
    },
    ref
  ) => {
    const internalInputRef = useRef(null);
    const styles = useMultiStyleConfig("Select", { size });

    const resolveOptionValue = useCallback(
      (option: any): string => {
        return valueKey && option ? option[valueKey] : option ? option : "";
      },
      [valueKey]
    );

    const resolveOptionLabel = useCallback(
      (option: any): string => {
        return labelKey ? option[labelKey] : option ? option : "";
      },
      [labelKey]
    );

    const onOptionChange = useCallback(
      (selected: string | string[]) => {
        if (multiple && Array.isArray(selected)) {
          const selectedOptions = options.filter((option) =>
            selected.includes(resolveOptionValue(option))
          );
          if (onChange) {
            onChange(selectedOptions);
          }
        } else {
          const selectedOption = options.find(
            (option) => resolveOptionValue(option) === selected
          );
          if (onChange) {
            onChange(selectedOption);
          }
        }
      },
      [options, multiple, onChange, resolveOptionValue]
    );

    const selectedValue = useMemo(() => {
      if (!value) {
        return "";
      }

      if (multiple && Array.isArray(value)) {
        return value.map((option) => resolveOptionValue(option));
      } else {
        return resolveOptionValue(value);
      }
    }, [value, multiple, resolveOptionValue]);

    const selectedLabel = useMemo((): string => {
      if (defaultValue && !value) {
        return resolveOptionLabel(defaultValue);
      }
      if (!value) {
        return "";
      }

      if (multiple && Array.isArray(value)) {
        return value.map((option) => resolveOptionLabel(option)).join(", ");
      } else {
        return resolveOptionLabel(value);
      }
    }, [value, multiple, resolveOptionLabel, defaultValue]);

    const onCloseMenu = useCallback(() => internalInputRef.current.focus(), [
      internalInputRef,
    ]);

    const refs = useMergeRefs(internalInputRef, ref);

    return (
      <FormControl>
        <Menu
          onClose={onCloseMenu}
          closeOnSelect={!multiple}
          defaultIsOpen={false}
          matchWidth
          modifiers={[
            {
              name: "preventOverflow",
              options: {
                altAxis: true,
              },
            },
          ]}
          size={size}
          {...menuProps}
        >
          <MenuButton
            width={isFullWidth ? "100%" : "inherit"}
            type="button"
            disabled={isDisabled}
          >
            <TextField
              isDisabled={isDisabled}
              isTruncated
              sx={{ ...styles.root, ...sx }}
              rightIcon={!isDisabled && <ArrowDownIcon />}
              justifyContent="flex-start"
              variant="outline"
              value={selectedLabel}
              ref={refs}
              placeholder={placeholder}
              isReadOnly
              label={label}
              size={size}
              {...rest}
            />
          </MenuButton>
          <MenuList zIndex={20} maxHeight="100vh" overflowY="auto" {...menuListProps}>
            <MenuOptionGroup
              defaultValue={resolveOptionValue(defaultValue)}
              type={multiple ? "checkbox" : "radio"}
              onChange={onOptionChange}
              value={selectedValue}
            >
              {placeholder && (
                <MenuItemOption isDisabled>{placeholder}</MenuItemOption>
              )}
              {options.map((option, i) => (
                <MenuItemOption key={i} value={resolveOptionValue(option)}>
                  {resolveOptionLabel(option)}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Menu>
      </FormControl>
    );
  }
);
